const { makeAction } = require('utils/action_helpers');
const librarianService = require('services/librarian_service');
const librarianReadingService = require('services/librarian_reading_service');

const DEFAULT_DATA = {
  sessionId: null,
  messages: [],
  availableFilters: {},
  streamingEnabled: false,
  theme: 'classic',
  activeFilters: {},
};

let activeStream = null;
let activeRequestId = null;

const classroomLibrarianEnabled = (store) => {
  const classroomState = store && store.classroom;
  const classroomData = classroomState && classroomState.data;
  if (!classroomData) {
    return false;
  }
  return Boolean(classroomData.librarianEnabled);
};

const classroomQandAEnabled = (store) => {
  const classroomState = store && store.classroom;
  const classroomData = classroomState && classroomState.data;
  if (!classroomData) {
    return false;
  }
  return Boolean(classroomData.librarianQandAEnabled);
};

const classroomVideoEnabled = (store) => {
  const classroomState = store && store.classroom;
  const classroomData = classroomState && classroomState.data;
  if (!classroomData) {
    return false;
  }
  return classroomData.librarianVideoEnabled !== false;
};

const featureEnabled = (store) =>
  typeof window !== 'undefined' &&
  Boolean(window.ENABLE_LIBRARIAN_UI) &&
  classroomLibrarianEnabled(store);

const getStoreSlice = (store) => store.librarianChat || {};
const getChatData = (store) => getStoreSlice(store).data || DEFAULT_DATA;
const getUserId = (store) => {
  const session = store.session;
  if (!session || !session.data || !session.data.login) {
    return undefined;
  }
  return session.data.login.id;
};
const getClassroomId = (store) => {
  const session = store.session;
  if (!session || !session.data) {
    return undefined;
  }
  return session.data.classroomId || session.data.classroom_id;
};

const getPrimaryRole = (store) => {
  const session = store && store.session;
  const login = session && session.data && session.data.login;
  const roles = login && Array.isArray(login.roles) ? login.roles : [];
  return roles.length ? roles[0] : null;
};

const isStudentRole = (store) => getPrimaryRole(store) === 'student';

const cloneRecommendations = (items = []) => items.map((rec) => Object.assign({}, rec));
const clonePlain = (value) => {
  if (!value || typeof value !== 'object') {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return Array.isArray(value) ? value.slice() : Object.assign({}, value);
  }
};

const stringValue = (value) => `${value || ''}`.trim();
const firstString = (...values) => {
  for (let index = 0; index < values.length; index += 1) {
    const cleaned = stringValue(values[index]);
    if (cleaned) {
      return cleaned;
    }
  }
  return '';
};

const normalizeContinuation = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const question = firstString(value.question, value.text, value.message);
  if (!question) {
    return null;
  }
  return {
    question,
    topic: firstString(value.topic),
    source_asset_ids: Array.isArray(value.source_asset_ids)
      ? value.source_asset_ids.map(stringValue).filter(Boolean)
      : [],
  };
};

const normalizeContinuations = (value) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return {
    adjacent: normalizeContinuation(source.adjacent),
    deeper: normalizeContinuation(source.deeper),
  };
};

const hasQaFields = (value) => Boolean(
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  ('answer' in value || 'deep_answer' in value || 'deepAnswer' in value || 'qa_response' in value)
);

const normalizeQaContent = (value = {}) => {
  const qa = value.qa_response || value.response || value.payload || value;
  const sources = Array.isArray(qa.sources)
    ? qa.sources
    : Array.isArray(value.sources)
      ? value.sources
      : [];
  return {
    type: 'qa',
    answer: firstString(qa.answer, value.answer, qa.text, value.text, qa.message, value.message),
    key_points: Array.isArray(qa.key_points)
      ? qa.key_points.map(stringValue).filter(Boolean)
      : Array.isArray(qa.bullets)
        ? qa.bullets.map(stringValue).filter(Boolean)
        : [],
    deep_answer: firstString(qa.deep_answer, qa.deepAnswer, value.deep_answer, value.deepAnswer),
    sources,
    continuations: normalizeContinuations(qa.continuations || value.continuations),
  };
};

const normalizeRecommendationContent = (value = {}) => ({
  type: 'recommendations',
  header: firstString(value.header, value.text, value.message),
  footer: firstString(value.footer),
  items: Array.isArray(value.items)
    ? value.items
    : Array.isArray(value.recommendations)
      ? value.recommendations
      : [],
});

const normalizeContent = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.content && typeof value.content === 'object' && !Array.isArray(value.content)) {
      return normalizeContent(value.content);
    }
    const type = `${value.type || ''}`.trim().toLowerCase();
    if (type === 'qa' || type === 'q&a' || hasQaFields(value)) {
      return normalizeQaContent(value);
    }
    if (type === 'recommendations' || type === 'recommendation' || Array.isArray(value.items)) {
      return normalizeRecommendationContent(value);
    }
    if (type === 'message' || 'text' in value || 'message' in value) {
      return Object.assign({}, value, {
        type: 'message',
        text: firstString(value.text, value.message, value.answer),
      });
    }
  }
  return { type: 'message', text: stringValue(value) };
};

const emptyQaContent = () => ({
  type: 'qa',
  answer: '',
  key_points: [],
  deep_answer: '',
  continuations: { adjacent: null, deeper: null },
  sources: [],
});

const emptyRecommendationContent = () => ({
  type: 'recommendations',
  header: '',
  footer: '',
  items: [],
});

const contentFromLegacyMessage = (message = {}) => {
  const qaResponse = message.qa_response || (message.meta && message.meta.qa_response);
  if (qaResponse && typeof qaResponse === 'object' && !Array.isArray(qaResponse)) {
    return normalizeQaContent(Object.assign({}, qaResponse, {
      sources: Array.isArray(qaResponse.sources) ? qaResponse.sources : message.recommendations,
    }));
  }
  if (Array.isArray(message.recommendations) && message.recommendations.length) {
    return normalizeRecommendationContent({
      header: message.meta && message.meta.header,
      footer: message.meta && message.meta.footer,
      items: message.recommendations,
    });
  }
  return { type: 'message', text: firstString(message.text, message.message) };
};

const normalizeMessage = (message = {}) => {
  const content = normalizeContent(message.content || contentFromLegacyMessage(message));
  const recommendations = Array.isArray(message.recommendations)
    ? cloneRecommendations(message.recommendations)
    : content.type === 'recommendations'
      ? cloneRecommendations(content.items)
      : content.type === 'qa'
        ? cloneRecommendations(content.sources)
        : [];
  return Object.assign({}, message, {
    role: message.role || 'assistant',
    text: firstString(message.text, content.text, content.answer, content.header),
    content,
    recommendations,
    meta: message.meta && typeof message.meta === 'object' ? Object.assign({}, message.meta) : {},
  });
};

const cloneMessage = (message) => {
  const clone = Object.assign({}, message);
  clone.meta = message && message.meta ? Object.assign({}, message.meta) : undefined;
  clone.content = normalizeContent(clonePlain(message && message.content ? message.content : contentFromLegacyMessage(message)));
  clone.recommendations = cloneRecommendations((message && message.recommendations) || []);
  return normalizeMessage(clone);
};
const cloneMessages = (messages = []) => messages.map(cloneMessage);

const mapPayloadToData = (payload = {}, existing = {}) => {
  const messages = Array.isArray(payload.messages)
    ? cloneMessages(payload.messages)
    : cloneMessages(existing.messages || []);
  return {
    sessionId: payload.sessionId || existing.sessionId || null,
    messages,
    availableFilters: payload.availableFilters || existing.availableFilters || {},
    streamingEnabled: payload.streamingEnabled !== undefined
      ? Boolean(payload.streamingEnabled)
      : Boolean(existing.streamingEnabled),
    theme: payload.theme || existing.theme || 'classic',
    activeFilters: existing.activeFilters || {},
  };
};

const setData = (setState, data, overrides = {}) => {
  const payload = Object.assign({ data }, overrides);
  setState('librarianChat', payload);
};

const setStatusOnly = (setState, getStore, overrides = {}) => {
  const store = getStore();
  const slice = getStoreSlice(store);
  const snapshot = getChatData(store);
  setData(setState, snapshot, Object.assign({
    loading: slice.loading,
    status: slice.status,
    error: slice.error,
  }, overrides));
};

const closeActiveStream = () => {
  if (activeStream) {
    activeStream.close();
    activeStream = null;
  }
  activeRequestId = null;
};

const parseEventPayload = (raw) => {
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const updateAssistantMessage = ({ setState, getStore, status, mutate }) => {
  const store = getStore();
  const data = getChatData(store);
  const messages = cloneMessages(data.messages);
  const index = messages.length - 1;
  if (index < 0 || messages[index].role !== 'assistant') {
    return;
  }
  mutate(messages[index]);
  const updatedData = Object.assign({}, data, { messages });
  setData(setState, updatedData, { status: status || getStoreSlice(store).status });
};

const mergeRecommendationItems = (payload = {}) => {
  const summaries = Array.isArray(payload.recommendations) ? payload.recommendations : [];
  const items = Array.isArray(payload.items) ? payload.items : [];
  if (!summaries.length) {
    return items;
  }
  if (!items.length) {
    return summaries;
  }
  const itemsByAssetId = {};
  items.forEach((item) => {
    const assetId = stringValue(item && item.asset_id);
    if (assetId) {
      itemsByAssetId[assetId] = item;
    }
  });
  const merged = summaries.map((summary) => {
    const assetId = stringValue(summary && summary.asset_id);
    return Object.assign({}, summary, itemsByAssetId[assetId] || {});
  });
  items.forEach((item) => {
    const assetId = stringValue(item && item.asset_id);
    if (!assetId || merged.some((candidate) => stringValue(candidate.asset_id) === assetId)) {
      return;
    }
    merged.push(item);
  });
  return merged;
};

const replaceWithCompletedTurn = ({ setState, getStore, payload }) => {
  const finalMessages = Array.isArray(payload && payload.messages)
    ? payload.messages.map(normalizeMessage)
    : [];
  if (!finalMessages.length) {
    updateAssistantMessage({
      setState,
      getStore,
      status: 'idle',
      mutate: (messageDraft) => {
        messageDraft.meta = Object.assign({}, messageDraft.meta || {}, {
          pending: false,
          streaming: false,
        });
      },
    });
    return;
  }
  const store = getStore();
  const data = getChatData(store);
  const messages = cloneMessages(data.messages);
  const nextMessages = finalMessages.length <= 2 && messages.length >= finalMessages.length
    ? messages.slice(0, messages.length - finalMessages.length).concat(finalMessages)
    : finalMessages;
  const updatedData = Object.assign({}, data, {
    sessionId: payload.sessionId || data.sessionId,
    messages: nextMessages,
    availableFilters: payload.availableFilters || data.availableFilters || {},
    streamingEnabled: payload.streamingEnabled !== undefined
      ? Boolean(payload.streamingEnabled)
      : data.streamingEnabled,
  });
  setData(setState, updatedData, { status: 'idle', error: undefined });
};

const startStreaming = ({ message, filters, setState, getStore }) => {
  return new Promise((resolve) => {
    closeActiveStream();
    const store = getStore();
    const userId = getUserId(store);
    const classroomId = getClassroomId(store);
    const enableQA = classroomQandAEnabled(store);
    const enabledVideo = classroomVideoEnabled(store);
    const data = getChatData(store);
    const messages = cloneMessages(data.messages);
    messages.push(normalizeMessage({ role: 'user', content: { type: 'message', text: message }, recommendations: [] }));
    messages.push(normalizeMessage({
      role: 'assistant',
      content: { type: 'message', text: '' },
      recommendations: [],
      meta: { pending: true, streaming: true },
    }));
    const pendingData = Object.assign({}, data, { messages });
    setData(setState, pendingData, { status: 'streaming', error: undefined });

    const requestId = Date.now();
    activeRequestId = requestId;

    try {
      activeStream = librarianService.openChatStream({
        message,
        filters,
        userId,
        classroomId,
        enableQA,
        enabledVideo,
      });
    } catch (error) {
      closeActiveStream();
      const snapshot = getChatData(getStore());
      setData(setState, snapshot, { error: error.message, status: 'error' });
      resolve();
      return;
    }

  const isStale = () => activeRequestId !== requestId;

  const finalize = (overrides = {}) => {
    if (isStale()) {
      return;
    }
    closeActiveStream();
    const storeSnapshot = getStore();
    const currentData = getChatData(storeSnapshot);
    setData(setState, currentData, { status: overrides.status || 'idle', error: overrides.error });
    resolve();
  };

  activeStream.addEventListener('turn_started', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    if (!payload.sessionId) {
      return;
    }
    const currentData = getChatData(getStore());
    setData(setState, Object.assign({}, currentData, { sessionId: payload.sessionId }), { status: 'streaming' });
  });

  activeStream.addEventListener('message_started', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    const messageType = `${payload.type || payload.contentType || 'message'}`.trim().toLowerCase();
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        if (messageType === 'qa') {
          messageDraft.content = emptyQaContent();
          messageDraft.recommendations = [];
        } else if (messageType === 'recommendations') {
          messageDraft.content = emptyRecommendationContent();
          messageDraft.recommendations = [];
        } else {
          messageDraft.content = { type: 'message', text: '', reason: payload.reason };
          messageDraft.recommendations = [];
        }
        messageDraft.text = '';
        messageDraft.meta = Object.assign({}, messageDraft.meta || {}, {
          pending: true,
          streaming: true,
          streamType: messageType,
        });
      },
    });
  });

  activeStream.addEventListener('qa_answer', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        const current = messageDraft.content && messageDraft.content.type === 'qa' ? messageDraft.content : emptyQaContent();
        messageDraft.content = Object.assign({}, current, { answer: stringValue(payload.answer) });
        messageDraft.text = messageDraft.content.answer;
      },
    });
  });

  activeStream.addEventListener('qa_key_points', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        const current = messageDraft.content && messageDraft.content.type === 'qa' ? messageDraft.content : emptyQaContent();
        messageDraft.content = Object.assign({}, current, {
          key_points: Array.isArray(payload.key_points) ? payload.key_points.map(stringValue).filter(Boolean) : [],
        });
      },
    });
  });

  activeStream.addEventListener('qa_deep_answer', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        const current = messageDraft.content && messageDraft.content.type === 'qa' ? messageDraft.content : emptyQaContent();
        messageDraft.content = Object.assign({}, current, { deep_answer: stringValue(payload.deep_answer) });
      },
    });
  });

  activeStream.addEventListener('qa_continuations', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        const current = messageDraft.content && messageDraft.content.type === 'qa' ? messageDraft.content : emptyQaContent();
        messageDraft.content = Object.assign({}, current, {
          continuations: normalizeContinuations(payload.continuations),
        });
      },
    });
  });

  activeStream.addEventListener('qa_sources', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    const sources = Array.isArray(payload.sources) ? payload.sources : [];
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        const current = messageDraft.content && messageDraft.content.type === 'qa' ? messageDraft.content : emptyQaContent();
        messageDraft.content = Object.assign({}, current, { sources });
        messageDraft.recommendations = cloneRecommendations(sources);
      },
    });
  });

  activeStream.addEventListener('recommendation_text', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        const current = messageDraft.content && messageDraft.content.type === 'recommendations'
          ? messageDraft.content
          : emptyRecommendationContent();
        messageDraft.content = Object.assign({}, current, {
          header: stringValue(payload.header),
          footer: stringValue(payload.footer),
        });
        messageDraft.text = messageDraft.content.header;
        messageDraft.meta = Object.assign({}, messageDraft.meta || {}, {
          header: messageDraft.content.header,
          footer: messageDraft.content.footer,
        });
      },
    });
  });

  activeStream.addEventListener('recommendations', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    const items = mergeRecommendationItems(payload);
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        const current = messageDraft.content && messageDraft.content.type === 'recommendations'
          ? messageDraft.content
          : emptyRecommendationContent();
        messageDraft.content = Object.assign({}, current, { items });
        messageDraft.recommendations = cloneRecommendations(items);
      },
    });
  });

  activeStream.addEventListener('message', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    updateAssistantMessage({
      setState,
      getStore,
      status: 'streaming',
      mutate: (messageDraft) => {
        messageDraft.text = stringValue(payload.text);
        messageDraft.content = { type: 'message', text: messageDraft.text, reason: payload.reason };
      },
    });
  });

  activeStream.addEventListener('message_complete', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    replaceWithCompletedTurn({ setState, getStore, payload });
    finalize({ status: 'idle' });
  });

  activeStream.addEventListener('session_error', (event) => {
    if (isStale()) {
      return;
    }
    const payload = parseEventPayload(event.data) || {};
    updateAssistantMessage({
      setState,
      getStore,
      status: 'error',
      mutate: (messageDraft) => {
        messageDraft.text = payload.message || 'The librarian could not save this session. Please try again.';
        messageDraft.content = { type: 'message', text: messageDraft.text, reason: payload.code || 'session_error' };
        messageDraft.meta = Object.assign({}, messageDraft.meta || {}, {
          pending: false,
          streaming: false,
        });
      },
    });
    finalize({ status: 'error', error: payload.message });
  });

  activeStream.onerror = () => {
    if (isStale()) {
      return;
    }
    finalize({ status: 'error', error: 'The librarian lost connection. Please try again.' });
  };
  });
};

const sendWithoutStreaming = async ({ message, filters, setState, getStore }) => {
  const store = getStore();
  const userId = getUserId(store);
  const classroomId = getClassroomId(store);
  const enableQA = classroomQandAEnabled(store);
  const enabledVideo = classroomVideoEnabled(store);
  const currentData = getChatData(store);
  const payload = await librarianService.sendChatMessage({
    message,
    filters,
    userId,
    classroomId,
    enableQA,
    enabledVideo,
  });
  const nextData = mapPayloadToData(payload, currentData);
  nextData.activeFilters = currentData.activeFilters;
  setData(setState, nextData, { status: 'idle', loading: false, error: undefined });
};

module.exports = {
  initialize: () =>
    makeAction(async (setState, dispatch, getStore) => {
      const store = getStore();
      if (!featureEnabled(store)) {
        return;
      }
      setStatusOnly(setState, getStore, { loading: true, error: undefined });
      try {
        const userId = getUserId(store);
        const classroomId = getClassroomId(store);
        const payload = await librarianService.fetchSession({ userId, classroomId });
        const nextData = mapPayloadToData(payload, getChatData(store));
        nextData.activeFilters = getChatData(store).activeFilters;
        setData(setState, nextData, { loading: false, status: 'idle', error: undefined });
      } catch (error) {
        setStatusOnly(setState, getStore, { loading: false, error: error.message, status: 'error' });
      }
    }),

  updateFilters: (filters) =>
    makeAction((setState, dispatch, getStore) => {
      const store = getStore();
      const data = getChatData(store);
      const updatedData = Object.assign({}, data, { activeFilters: filters });
      setData(setState, updatedData, { status: getStoreSlice(store).status });
    }),

  sendMessage: ({ message }) =>
    makeAction(async (setState, dispatch, getStore) => {
      const trimmed = (message || '').trim();
      if (!trimmed) {
        return;
      }
      const store = getStore();
      const data = getChatData(store);
      const filters = data.activeFilters || {};
      setData(setState, data, { loading: true, error: undefined });
      if (data.streamingEnabled) {
        await startStreaming({ message: trimmed, filters, setState, getStore });
        setStatusOnly(setState, getStore, { loading: false });
      } else {
        try {
          await sendWithoutStreaming({ message: trimmed, filters, setState, getStore });
        } catch (error) {
          setStatusOnly(setState, getStore, { loading: false, error: error.message, status: 'error' });
        }
      }
    }),

  resetConversation: () =>
    makeAction(async (setState, dispatch, getStore) => {
      closeActiveStream();
      const store = getStore();
      const data = getChatData(store);
      setData(setState, data, { loading: true });
      try {
        await librarianService.resetSession({ userId: getUserId(store), classroomId: getClassroomId(store) });
      } catch (error) {
        setStatusOnly(setState, getStore, { loading: false, error: error.message, status: 'error' });
        return;
      }
      const nextData = Object.assign({}, data, {
        messages: [],
        sessionId: data.sessionId,
      });
      setData(setState, nextData, { loading: false, status: 'idle', error: undefined });
    }),

  openReadingSession: ({ readingSessionId, assetId, title, openedAt }) =>
    makeAction(async (setState, dispatch, getStore) => {
      const store = getStore();
      if (!featureEnabled(store) || !isStudentRole(store)) {
        return;
      }

      const classroomId = getClassroomId(store);
      if (!classroomId || !readingSessionId || !assetId) {
        return;
      }

      try {
        await librarianReadingService.openReadingSession({
          classroomId,
          readingSessionId,
          assetId,
          title: title || null,
          openedAt: openedAt || new Date().toISOString(),
        });
      } catch (error) {
        console.warn('Failed to open librarian reading session', error);
      }
    }),

  closeReadingSession: ({ readingSessionId, closedAt }) =>
    makeAction(async (setState, dispatch, getStore) => {
      const store = getStore();
      if (!featureEnabled(store) || !isStudentRole(store)) {
        return;
      }

      const classroomId = getClassroomId(store);
      if (!classroomId || !readingSessionId) {
        return;
      }

      try {
        await librarianReadingService.closeReadingSession({
          classroomId,
          readingSessionId,
          closedAt: closedAt || new Date().toISOString(),
        });
      } catch (error) {
        console.warn('Failed to close librarian reading session', error);
      }
    }),

  dispose: () =>
    makeAction(() => {
      closeActiveStream();
    }),
};
